import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteEmployee, fetchEmployeesList } from "../../shared/organizationMastersApi";
import { IconEye, IconPlus } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

function formatLocation(employee, countries, states, cities) {
  const country = countries.find((item) => item.id === employee.countryId);
  const state = states.find((item) => item.id === employee.stateId);
  const city = cities.find((item) => item.id === employee.cityId);
  return [city?.name, state?.name, country?.name].filter(Boolean).join(", ") || "-";
}

export function EmployeesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const designationById = useMemo(() => {
    const m = new Map();
    for (const d of designations) m.set(d.id, d);
    return m;
  }, [designations]);

  const departmentById = useMemo(() => {
    const m = new Map();
    for (const d of departments) m.set(d.id, d);
    return m;
  }, [departments]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const dep = departmentById.get(x.departmentId);
      const des = designationById.get(x.designationId);
      return {
        id: x.id,
        employee: x,
        departmentName: dep?.name ?? "-",
        designationName: des?.name ?? "-",
        location: formatLocation(x, countries, states, cities)
      };
    });
  }, [items, departmentById, designationById, countries, states, cities]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [iRes, dRes, depRes, countryRes, stateRes, cityRes] = await Promise.all([
      fetchEmployeesList(),
      apiGet("/api/Designations"),
      apiGet("/api/Departments"),
      apiGet("/api/Countries"),
      apiGet("/api/States"),
      apiGet("/api/Cities")
    ]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    if (dRes.ok) setDesignations(dRes.data ?? []);
    if (depRes.ok) setDepartments(depRes.data ?? []);
    if (countryRes.ok) setCountries(countryRes.data ?? []);
    if (stateRes.ok) setStates(stateRes.data ?? []);
    if (cityRes.ok) setCities(cityRes.data ?? []);
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this employee?"))) return;
      const res = await deleteEmployee(id);
      if (!res.ok) setError(res.error ?? "Failed to delete employee");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 120,
        valueGetter: (_v, row) => row.employee.name ?? "-"
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.employee.email ?? "-"
      },
      {
        field: "departmentName",
        headerName: "Department",
        flex: 0.9,
        minWidth: 110,
        valueGetter: (_v, row) => row.departmentName
      },
      {
        field: "designationName",
        headerName: "Designation",
        flex: 0.9,
        minWidth: 110,
        valueGetter: (_v, row) => row.designationName
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.employee;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/employees?edit=${x.id}`)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Employees</h2>
          <p>{loading ? "Loading employees..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/employees" className="icon-button icon-button--toolbar" title="Add employee" aria-label="Add employee">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Employee details"
        title={viewRow?.employee?.name ?? "—"}
        subtitle={viewRow?.employee?.email}
        ariaLabel="Employee details"
        images={
          viewRow?.employee?.photo
            ? [{ label: "Photo", src: viewRow.employee.photo, linkText: "Open photo" }]
            : []
        }
        sections={
          viewRow
            ? [
                {
                  title: "Employee Details",
                  columns: 2,
                  fields: [
                    ["Name", viewRow.employee.name ?? "-"],
                    ["Email", viewRow.employee.email ?? "-"],
                    ["Phone", viewRow.employee.phoneNo ?? "-"],
                    ["Gender", viewRow.employee.gender ?? "-"],
                    ["Department", viewRow.departmentName],
                    ["Designation", viewRow.designationName]
                  ]
                },
                {
                  title: "Address Details",
                  fields: [
                    ["Address", viewRow.employee.address ?? "-", true],
                    ["Location", viewRow.location ?? "-"],
                    ["POBOX", viewRow.employee.pobox ?? "-"]
                  ]
                },
                {
                  title: "Contact Details",
                  columns: 2,
                  fields: [
                    ["Contact Person", viewRow.employee.contactPersonName ?? "-"],
                    ["Contact Mobile", viewRow.employee.contactPersonMob ?? "-"]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
